import React, { useMemo, useEffect } from 'react'
import { Box, Typography, ClickAwayListener, useTheme } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { components, StylesConfig } from 'react-select'
import Select from 'components/global/CUK/AsyncSelect/Select'
import StateIcon from 'components/global/StateIcon'
import { OPERATION_FIND_OFFERS } from '../operations-gql/queries'
import FieldLabel from './FieldLabel'
import ErrorMessage from './ErrorMessage'
import { trackEvent } from 'analytics'

interface Offer {
  id: number
  name: string
  duration_value: number
  duration_unit: string
  max_usage: number
}

interface OptionType {
  id: number
  value: number
  label: string
  max_usage: number
}

const DropdownIndicator = ({ ...props }) => {
  const theme = useTheme()
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon
          iconlabel={'angle-up'}
          iconweight={'regular'}
          iconcolor={theme.palette.primary.main}
        />
      ) : (
        <StateIcon iconweight={'regular'} iconlabel={'angle-down'} iconcolor={theme.palette.grey[500]} />
      )}
    </Box>
  )
}

const ValueContainer = ({ ...props }) => {
  const theme = useTheme()
  const { menuIsOpen } = props.selectProps
  const selectedValue = props.getValue()[0]

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <components.ValueContainer {...props}>
      <Box>
        <Typography
          sx={{
            color: menuIsOpen ? '#3B82F6' : theme.palette.grey[500],
            fontSize: '12px',
            paddingLeft: theme.spacing(1.5),
            fontWeight: 500,
          }}
        >
          {selectedValue 
            ? selectedValue.label 
            : props.selectProps.placeholder
          }
        </Typography>
      </Box>
    </components.ValueContainer>
  )
}

const Control = ({ children, ...props }) => {
  const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps
  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <components.Control {...props}>
      <ClickAwayListener
        mouseEvent='onMouseDown'
        onClickAway={() => onMenuClose()}
      >
        <Box
          onClick={() => menuIsOpen ? onMenuClose() : onMenuOpen()}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexGrow: 1,
            gap: 7,
            cursor: 'pointer'
          }}
        >
          {children}
        </Box>
      </ClickAwayListener>
    </components.Control>
  )
}

const createSelectStyles = (hasError: boolean): StylesConfig<OptionType> => ({
  control: (provided, state) => ({
    ...provided,
    borderColor: hasError ? '#f44336' : '#EFEFEF',
    '&:hover': {
      borderColor: hasError ? '#f44336' : '#EFEFEF'
    },
    boxShadow: state.isFocused 
      ? hasError ? '0 0 0 1px #f44336' : '0 0 0 1px #2570EB'
      : 'none'
  })
})

const OffersField: React.FC = () => {
  const { control, setValue, watch, formState: { errors } } = useFormContext()
  const { t } = useTranslation()
  const siteId = watch('site_id')
  
  const { data, loading, error } = useQuery(OPERATION_FIND_OFFERS, {
    variables: { sf_id: siteId },
    skip: !siteId
  })

  const options = useMemo(() => {
    if (!data?.offers) return []
    return data.offers.map((offer: Offer) => ({
      id: offer.id,
      value: offer.id,
      label: `${offer.name} : ${offer.duration_value} ${offer.duration_unit}`,
      max_usage: offer.max_usage,
    }))
  }, [data])

  useEffect(() => {
    setValue('with_offer', Boolean(data?.offers?.length))
  }, [data, setValue])

  if (!siteId || loading || error || !options.length) {
    return null
  }

  const errorMessage = errors.offer_id?.message
  const displayMessage = typeof errorMessage === 'string' ? errorMessage : undefined
  const hasError = Boolean(errorMessage)

  return (
    <Box>
      <FieldLabel labelKey="coupons.create.offers" />
      <Controller
        control={control}
        name="offer_id"
        render={({ field: { onChange, value } }) => (
          <Select
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            options={options}
            onChange={(selectedOption: OptionType | null) => {
              const offerId = selectedOption?.id
              onChange(offerId)
              if (selectedOption) {
                setValue('max_usage', selectedOption.max_usage)
              }
              trackEvent(selectedOption ? 'select_offer' : 'remove_selected_offer')
            }}
            mainField="label"
            keyField="id"
            placeholder={t('coupons.create.select_offer')}
            components={{
              Control,
              DropdownIndicator,
              ValueContainer,
              Placeholder: (props) => (
                <Box>
                  <components.Placeholder {...props} />
                </Box>
              ),
              IndicatorSeparator: null,
              ClearIndicator: null,
            }}
            value={options.find(option => option.id === value) || null}
            height={40}
            width="100%"
            styles={createSelectStyles(hasError)}
          />
        )}
      />
      <ErrorMessage message={displayMessage} />
    </Box>
  )
}

export default OffersField